"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheThorneMethodology() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const closing = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const steps = [{
    number: "01",
    name: "SUSTAINABLE SOURCING",
    description: "We meticulously audit every fiber, selecting only organic, regenerative textiles that minimize environmental impact while maximizing structural integrity.",
    meta: "PHASE 01 / MATERIAL SELECTION",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/textile_sourcing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/textile_sourcing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/textile_sourcing.png-wvc-sizes",
    alt: "Studio Thorne sustainable textile sourcing process"
  }, {
    number: "02",
    name: "TECHNICAL BLUEPRINTS",
    description: "Every garment begins as a clinical sketch, mapping out complex seam lines and ergonomic volumes before a single cut is made.",
    meta: "PHASE 02 / PATTERN DRAFTING",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/technical_sketch.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/technical_sketch.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/technical_sketch.png-wvc-sizes",
    alt: "Technical garment blueprint and pattern drafting"
  }, {
    number: "03",
    name: "PRECISION CUTTING",
    description: "Using industrial-grade precision, we translate blueprints into fabric, ensuring grain alignment and structural balance for every panel.",
    meta: "PHASE 03 / FABRIC PREPARATION",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/fabric_cutting.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/fabric_cutting.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/fabric_cutting.png-wvc-sizes",
    alt: "Precision fabric cutting in the atelier"
  }, {
    number: "04",
    name: "GARMENT CONSTRUCTION",
    description: "The assembly phase where technical engineering meets couture, utilizing reinforced stitching and hidden seams for a clean, architectural finish.",
    meta: "PHASE 04 / ASSEMBLY",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/garment_assembly.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/garment_assembly.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/garment_assembly.png-wvc-sizes",
    alt: "Expert garment construction and stitching"
  }, {
    number: "05",
    name: "DRAPING & FORM",
    description: "We refine the silhouette on the mannequin, adjusting the drape to ensure the garment moves with the body as a second skin.",
    meta: "PHASE 05 / FIT REFINEMENT",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/fabric_draping.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/fabric_draping.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/fabric_draping.png-wvc-sizes",
    alt: "Behind-the-scenes fabric draping process"
  }, {
    number: "06",
    name: "FINAL QUALITY AUDIT",
    description: "Each piece undergoes a rigorous inspection, verifying every stitch, button, and seam against our uncompromising Thorne standards.",
    meta: "PHASE 06 / FINALIZATION",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/quality_audit.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/quality_audit.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1700152/img/quality_audit.png-wvc-sizes",
    alt: "Final quality audit of a finished garment"
  }];
  return <section data-section-id="3453" id="the-thorne-methodology" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-44 px-4 sm:px-6 lg:px-10">
      {}
      <div ref={header.ref} className={`max-w-4xl mx-auto text-center transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex items-center justify-center gap-4 mb-8">
          <span className="h-px w-12 bg-primary" />
          <span className="font-default text-xs tracking-[0.3em] text-primary font-medium uppercase">
            The Thorne Methodology
          </span>
          <span className="h-px w-12 bg-primary" />
        </div>

        <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight text-foreground">
          From Concept
          <br />
          <span className="italic font-serif" style={{
          WebkitTextStroke: "1px hsl(25 10% 85%)",
          color: "transparent"
        }}>
            to Couture.
          </span>
        </h2>

        <p className="font-serif text-base md:text-lg text-muted-foreground max-w-xl mx-auto mt-8 leading-relaxed">
          Studio Thorne operates at the intersection of clinical engineering and high-fashion. 
          Every garment is a result of a rigorous, multi-stage process designed to redefine 
          the boundaries of modern streetwear.
        </p>
      </div>

      {}
      <div className="relative mt-24 md:mt-32 lg:mt-40 max-w-7xl mx-auto">
        {}
        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-border -translate-x-1/2" aria-hidden="true" />

        <div className="flex flex-col gap-20 md:gap-28 lg:gap-32">
          {steps.map((step, i) => {
          const isLeft = i % 2 === 0;
          return <TimelineRow key={step.number} data-index={i} step={step} isLeft={isLeft} />;
        })}
        </div>
      </div>

      {}
      <div ref={closing.ref} className={`max-w-3xl mx-auto text-center mt-24 md:mt-32 lg:mt-40 transition-all duration-1000 ease-out ${closing.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h3 className="font-default font-bold text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-foreground">
          The Archive —
          <br />
          <span className="italic font-serif" style={{
          WebkitTextStroke: "1px hsl(25 10% 85%)",
          color: "transparent"
        }}>
            is now open.
          </span>
        </h3>

        <div className="mt-10 flex justify-center">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg px-8 h-14 text-sm tracking-[0.2em] uppercase font-default font-bold">
            <Link to="/CollectionsPage">
              Download Line Sheet
              <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}
type TimelineRowProps = {
  step: {
    number: string;
    name: string;
    description: string;
    meta: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  isLeft: boolean;
  "data-index"?: number;
};
function TimelineRow({
  step,
  isLeft,
  ...rest
}: TimelineRowProps) {
  const row = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={row.ref} {...rest} className={`relative transition-all duration-1000 ease-out ${row.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="hidden lg:block absolute left-1/2 top-12 -translate-x-1/2 z-10" aria-hidden="true">
        <span className="relative flex size-4">
          <span className="absolute inline-flex h-full w-full animate-ping bg-primary opacity-60" />
          <span className="relative inline-flex size-4 bg-primary shadow-md" />
        </span>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {}
        <div className={`${isLeft ? "lg:col-start-1 lg:pr-16" : "lg:col-start-2 lg:order-2 lg:pl-16"}`}>
          <div className="border border-border bg-card p-8 md:p-10 shadow-lg max-w-md mx-auto lg:mx-0" style={{
          marginLeft: isLeft ? undefined : "auto"
        }}>
            <div className="font-default font-bold text-5xl md:text-6xl text-primary leading-none tracking-tight tabular-nums">
              {step.number}
            </div>

            <h4 className="font-default font-bold text-xl md:text-2xl uppercase tracking-wide text-foreground mt-6">
              {step.name}
            </h4>

            <div className="h-px w-12 bg-primary mt-4" />

            <p className="font-serif text-sm md:text-base text-muted-foreground leading-relaxed mt-5">
              {step.description}
            </p>

            <div className="mt-8 pt-5 border-t border-border">
              <span className="font-default text-[11px] tracking-[0.25em] text-muted-foreground uppercase">
                {step.meta}
              </span>
            </div>
          </div>
        </div>

        {}
        <div className={`${isLeft ? "lg:col-start-2 lg:pl-16" : "lg:col-start-1 lg:order-1 lg:pr-16"}`}>
          <div className={`relative max-w-sm mx-auto lg:mx-0 ${isLeft ? "" : "lg:ml-auto"}`}>
            <div className="border border-border shadow-lg overflow-hidden aspect-square bg-card">
              <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full h-full object-cover grayscale contrast-125" />
            </div>
          </div>
        </div>
      </div>
    </div>;
}